import { DirtyModel } from "@models/interfaces/dirty_model";
import { Time } from "@models/time";
import { Trade } from "@models/trade";
import { WithTime } from "@models/interfaces/with_time";
import { HasSocketEvent } from "@models/interfaces/has_socket_event";
import { BestFeeCollection } from "@models/best_fee_collection";
import { MerchantDetails } from "@models/merchant_details";
import { FiatTransaction } from "@models/transaction";
import { SocketEvent } from "@helpers/types";
import { SHARE_TRANSACTION_MAX_MONTH_LIMIT } from "@helpers/constants";
import { TaxWithheld } from "@models/ui-components/tax-withheld";

export interface IFeeTransparencyTX {
	fee_percentage: string;
	fee_amount_in_fiat: string;
	is_fee_transparent: boolean;
}

export abstract class Transaction extends DirtyModel implements WithTime, HasSocketEvent {
	protected status: string = "";
	protected type: string = "";
	protected in_or_out: string = "";
	protected amount: string = "";
	protected time: Time = new Time();
	protected cryptocoin_id: string = "";
	protected last_changed: Time = new Time();
	protected tags: TransactionTag[] = []; // Only works without instancing the object inside the array, weird unknown black magic

	protected trade: Trade = new Trade();
	protected wallet_id = "";
	private relatedTransactions: Transaction[] = [];

	protected confirmed: boolean = false;
	protected is_savings: boolean = false;
	protected is_swap: boolean = false;
	protected is_bfc: boolean = false;
	protected is_index: boolean = false;
	protected is_card: boolean = false;
	protected merchant_details: MerchantDetails = new MerchantDetails();
	protected card_fiat_wallet_transaction: FiatTransaction = null;
	protected fiat_id: string = "";

	protected confirmation_by: string = "";
	protected recipient: string = "";
	protected from: string = "";
	protected corporate_action_asset_id: string = "";
	protected bfcFiatId: string = "";
	protected bfcFeeAmount: string = "";
	protected bfcRelatedWalletId: string = "";
	protected cryptocoin_symbol: string = "";
	protected destination_tag: string = "";

	public abstract isCancelable(): boolean;

	public eventData: SocketEvent = "";
	protected best_fee_collection: BestFeeCollection = null;

	public abstract hasAction(cancel: boolean): boolean;

	public tax_withheld: TaxWithheld = new TaxWithheld();

	//fee transparency
	protected fee_percentage = "";
	protected fee_amount_in_fiat = "";
	protected is_fee_transparent = false;

	public getRelatedTransactions(): Transaction[] {
		return this.relatedTransactions;
	}

	public setRelatedTransactions(trades: Transaction[]): void {
		this.relatedTransactions = trades;
	}

	public getCurrencyId(): string {
		return this.cryptocoin_id;
	}

	public isRebalancingTx(): boolean {
		return this.type === "rebalance";
	}

	public getWalletId(): string {
		return this.wallet_id;
	}

	public setWalletId(id: string): void {
		this.wallet_id = id;
	}

	public getAmount(): number {
		return +this.amount;
	}

	public setAmount(n: number): void {
		this.amount = n + "";
	}

	public getBfcRelatedWalletId(): string {
		return this.bfcRelatedWalletId;
	}

	public setBfcRelatedWalledId(id: string): void {
		this.bfcRelatedWalletId = id;
	}

	public setCurrencyId(s: string): void {
		this.cryptocoin_id = s;
	}

	public isUncredited(): boolean {
		const credited =
			this.isStatus("finished") ||
			(this.isType("withdrawal") && this.isStatus("processing")) ||
			(this.isType("withdrawal") && this.isStatus("open_invitation") && this.isConfirmed());
		return !credited;
	}

	public getEvent(): string {
		return this.eventData;
	}
	public isOpenInvitation(): boolean {
		return this.isStatus("open_invitation");
	}

	public getFiatId(): string {
		return this.fiat_id;
	}

	public isIndex(): boolean {
		return this.is_index;
	}

	public setIsIndex(b: boolean): void {
		this.is_index = b;
	}

	public setFiatId(f: string): void {
		this.fiat_id = f;
	}

	public setLastChangedTime(t: Time): void {
		this.last_changed = t;
	}

	public setTime(t: Time): void {
		this.time = t;
	}

	public setBfcFiatId(fiatId: string): void {
		this.bfcFiatId = fiatId;
	}

	public getBfcFiatId(): string {
		return this.bfcFiatId;
	}

	public setBfcFeeAmount(amount: string): void {
		this.bfcFeeAmount = amount;
	}

	public getBfcFeeAmount(): string {
		return this.bfcFeeAmount;
	}

	public isNew(): boolean {
		return this.getId() === "";
	}

	public getStatus(): string {
		return this.status;
	}

	public setBestFeeCollectionTransaction(bfc: BestFeeCollection): void {
		this.best_fee_collection = bfc;
	}

	public setBestFeeCollectionTrade(bfc: BestFeeCollection): void {
		this.getTrade().setBestFeeCollectionEntity(bfc);
	}

	public getIsBfc(): boolean {
		return this.is_bfc;
	}

	public setStatus(val: string): void {
		this.status = val;
	}

	public isStatus(s: string): boolean {
		return this.status === s;
	}

	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	public getBestFeeCollection(): any {
		return this.best_fee_collection;
	}

	public isType(t: string): boolean {
		return this.type === t;
	}

	public setInOrOut(s: string): void {
		this.in_or_out = s;
	}

	public isVsnFeeCollection(): boolean {
		return !!this.getTrade()?.getVsnFeeCollection();
	}

	public getType(): string {
		return this.type;
	}

	public isRefund(): boolean {
		return this.type === "refund";
	}

	public setType(val: string): void {
		this.type = val;
	}

	public isConfirmed(): boolean {
		return this.confirmed;
	}

	public getRecipient(): string {
		return this.recipient;
	}

	public setRecipient(val: string): void {
		this.recipient = val;
	}

	public hasRecipient(): boolean {
		return this.recipient !== "";
	}

	public getFrom(): string {
		return this.from;
	}

	public hasFrom(): boolean {
		return this.from !== "";
	}

	public getDividendAssetId(): string {
		return this.corporate_action_asset_id;
	}

	public getInOrOut(): string {
		return this.in_or_out;
	}

	public isOutgoing(): boolean {
		return this.in_or_out === "outgoing";
	}

	public isIncoming(): boolean {
		return this.in_or_out === "incoming";
	}

	public getTransactionTags(): TransactionTag[] {
		return this.tags;
	}

	public hasDividendId(): boolean {
		return !!this.corporate_action_asset_id;
	}

	public emailConfirmed(): boolean {
		return this.confirmed;
	}

	public isConfirmationRequired(): boolean {
		return this.confirmation_by !== "not_required";
	}

	public getTime(): Time {
		return this.time;
	}

	public isNotification(): boolean {
		return this.isStatus("pending") || this.isStatus("processing");
	}

	public isPotentialFraud(): boolean {
		return this.isStatus("potential_fraud");
	}

	public hasTrade(): boolean {
		return this.trade.getId() !== "";
	}

	public getTrade(): Trade {
		return this.trade;
	}

	public setTrade(t: Trade): void {
		this.trade = t;
	}

	public isWithdrawal(): boolean {
		return this.isType("withdrawal") || this.isType("transfer");
	}

	public isWithdrawalFraud(): boolean {
		return this.isWithdrawal() && this.isPotentialFraud();
	}

	public isTransfer(): boolean {
		return this.isType("transfer");
	}

	public isCanceled(): boolean {
		return this.isStatus("canceled");
	}

	public getLastChanged(): Time {
		return this.last_changed;
	}

	public get isSavings(): boolean {
		return this.is_savings;
	}

	public setIsSavings(b: boolean): void {
		this.is_savings = b;
	}

	public isSwap(): boolean {
		return this.is_swap;
	}

	public isCard(): boolean {
		return this.is_card;
	}

	public getMerchantDetails(): MerchantDetails {
		return this.merchant_details;
	}

	public getDebitCardRelatedTx(): FiatTransaction {
		return this.card_fiat_wallet_transaction;
	}

	public hasDebitCardRelatedTx(): boolean {
		return !!this.card_fiat_wallet_transaction && this.card_fiat_wallet_transaction.getId() !== "";
	}

	public canBeShared(): boolean {
		return !this.isOlderThanLimit() && this.isBuySellOrSwap();
	}

	private isOlderThanLimit(): boolean {
		const transactionDate = this.time.date();
		const elevenMonthsAgo = new Date();
		elevenMonthsAgo.setMonth(elevenMonthsAgo.getMonth() - SHARE_TRANSACTION_MAX_MONTH_LIMIT);
		return +elevenMonthsAgo > +transactionDate;
	}

	private isBuySellOrSwap(): boolean {
		return this.isType("buy") || this.isType("sell") || this.isType("swap");
	}

	public getTagShortNames(): string[] {
		if (this.tags.length > 0) {
			return this.tags.map((t) => t.attributes.short_name);
		}
		return [];
	}

	public getCardTxFeeTransparency(): Partial<IFeeTransparencyTX> {
		return {
			fee_percentage: this.fee_percentage ? this.fee_percentage : "0",
			fee_amount_in_fiat: this.fee_amount_in_fiat ? this.fee_amount_in_fiat : "0",
			is_fee_transparent: this.is_fee_transparent
		};
	}

	public getCryptoCoinSymbol(): string {
		return this.cryptocoin_symbol;
	}

	public getDestinationTagValue(): string {
		return this.destination_tag;
	}
}

export class TransactionTag {
	public constructor(type: string = "", attributes: TransactionAttributes = new TransactionAttributes()) {
		this.type = type;
		this.attributes = attributes;
	}
	public type: string = "";
	public attributes: TransactionAttributes = new TransactionAttributes();
}

export class TransactionAttributes {
	public constructor(short_name: string = "", name: string = "") {
		this.short_name = short_name;
		this.name = name;
	}

	public short_name: string = "";
	public name: string = "";
}
